import type { Address } from '@medplum/fhirtypes'
import { standardizeAddressToken } from '#src/models/patients/uspsAbbreviations.ts'

// Mirrors SPLIT_RE in packages/scripts/scripts/move-apartment-info-to-line2.ts.
// `@seen/core` cannot depend on `@seen/scripts`, so the regex and splitting
// helpers are duplicated here.
const SPLIT_RE =
  /(?<pre>[\s,][\s,.]*)(?<kw>apartment|apt\.?|unit|room|rm\.?|#)(?=\s|\d|[A-Za-z]|$)/i

interface SplitResult {
  line1New: string
  unitInfo: string
}

function splitAddressLine(line: string): SplitResult | null {
  const match = SPLIT_RE.exec(line)
  if (!match?.groups) {
    return null
  }

  const kw = match.groups.kw
  const pre = match.groups.pre
  const kwStart = match.index + pre.length

  const before = line.slice(0, match.index)
  if (!/[A-Za-z0-9]/.test(before)) {
    return null
  }

  const line1New = before.replace(/[\s,]+$/, '').trim()
  const unitInfoRaw = line.slice(kwStart).trim()
  const unitInfo = unitInfoRaw.replace(/[\s,]+$/, '').trim()

  const afterKw = unitInfo.slice(kw.length).trim()
  if (!afterKw) {
    return null
  }

  return { line1New, unitInfo }
}

function mergeLine2(unitInfo: string, existing: string | undefined): string {
  const trimmed = existing?.trim()
  if (!trimmed) {
    return unitInfo
  }
  if (trimmed.toLowerCase() === unitInfo.toLowerCase()) {
    return trimmed
  }
  return `${unitInfo}, ${trimmed}`
}

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

const DIRECTIONAL_ABBREVIATIONS = new Set(['NE', 'SE', 'SW', 'NW'])

function titleCaseToken(token: string): string {
  if (!token) {
    return token
  }
  if (/\d/.test(token)) {
    return token
  }
  if (DIRECTIONAL_ABBREVIATIONS.has(token.toUpperCase())) {
    return token.toUpperCase()
  }
  return token[0].toUpperCase() + token.slice(1).toLowerCase()
}

function titleCase(value: string): string {
  return collapseWhitespace(value).split(' ').map(titleCaseToken).join(' ')
}

function normalizePostalCode(postalCode: string): string {
  const trimmed = postalCode.trim()
  const zipPlus4 = trimmed.match(/^(\d{5})-?(\d{4})$/)
  if (zipPlus4) {
    return `${zipPlus4[1]}-${zipPlus4[2]}`
  }
  const zip5 = trimmed.match(/^(\d{5})/)
  if (zip5) {
    return zip5[1]
  }
  return trimmed
}

function hasMeaningfulContent(address: Address): boolean {
  return Boolean(
    address.line?.some((l) => l?.trim()) ||
    address.city?.trim() ||
    address.state?.trim() ||
    address.postalCode?.trim()
  )
}

export function normalizeAddress(
  address: Address | undefined
): Address | undefined {
  if (!address || !hasMeaningfulContent(address)) {
    return undefined
  }

  const lines = (address.line ?? [])
    .map((l) => collapseWhitespace(l ?? ''))
    .filter((l) => l.length > 0)

  let line0 = lines[0] ?? ''
  let line1 = lines[1] ?? ''
  const extra = lines.slice(2)

  if (line0) {
    const split = splitAddressLine(line0)
    if (split) {
      line0 = split.line1New
      line1 = mergeLine2(split.unitInfo, line1 || undefined)
    }
  }

  const normalizedLines: string[] = []
  if (line0) {
    normalizedLines.push(titleCase(line0))
  }
  if (line1) {
    normalizedLines.push(titleCase(line1))
  }
  for (const e of extra) {
    normalizedLines.push(titleCase(e))
  }

  const result: Address = { ...address }
  if (normalizedLines.length > 0) {
    result.line = normalizedLines
  } else {
    delete result.line
  }
  if (address.city) {
    result.city = titleCase(address.city)
  }
  if (address.state) {
    result.state = collapseWhitespace(address.state).toUpperCase()
  }
  if (address.postalCode) {
    result.postalCode = normalizePostalCode(address.postalCode)
  }

  return result
}

// FNV-1a, widened to 53 bits. Pure JS so `@seen/core` stays a browser-safe leaf
// (no node:crypto). Two independent 32-bit FNV-1a passes (different seeds) are
// combined into a JS safe integer, giving ~9e15 buckets. This keeps the
// birthday-collision probability negligible at roster scale (thousands of
// Locations) — a collision would silently merge two distinct provider offices
// into one Location, the exact dedup failure `addressIdentifier` must avoid.
function fnv1a32(value: string, seed: number): number {
  let hash = seed
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

function fnv1a(value: string): string {
  const low = fnv1a32(value, 0x811c9dc5)
  const high = fnv1a32(value, 0x84222325)
  // Fold the high word into the top 21 bits of a 53-bit safe integer.
  const combined = (high % 0x200000) * 0x100000000 + low
  // base36 keeps the identifier short.
  return combined.toString(36)
}

// Stable identifier for an Address, used to dedup Location resources so multiple
// providers sharing a building converge on one Location. Built on
// `normalizeAddress`, then made deliberately lossy: punctuation/whitespace are
// stripped, postal code is truncated to 5 digits (prod stores both `91801` and
// `918013830` for the same site), and street/directional/unit words are
// standardized to USPS abbreviations. This is more aggressive than
// `addressesEqual` on purpose — the identifier is a dedup key, not an
// equality check.
export function addressIdentifier(
  address: Address | undefined
): string | undefined {
  const normalized = normalizeAddress(address)
  if (!normalized) {
    return undefined
  }
  const canonicalize = (value: string): string =>
    value
      .replace(/[.,#]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase()
      .split(' ')
      .map(standardizeAddressToken)
      .join(' ')
  const key = [
    canonicalize((normalized.line ?? []).join(' ')),
    canonicalize(normalized.city ?? ''),
    canonicalize(normalized.state ?? ''),
    (normalized.postalCode ?? '').slice(0, 5),
  ].join('|')
  return `addr-${fnv1a(key)}`
}

const UNIT_DESIGNATORS = new Set([
  'APT',
  'STE',
  'UNIT',
  'RM',
  'PMB',
  'BLDG',
  'FL',
  'DEPT',
])

// Key for "same building": two addresses with equal keys need no ride between
// them. Units, suites, and mailboxes are dropped (both the second line and a
// trailing designator on the street line), and state is left out because the
// 5-digit ZIP already fixes it, so `California` and `CA` compare equal. A
// designator is only cut at token 2 or later so `100 Floor St` keeps its street.
export function buildingAddressKey(
  address: Address | undefined
): string | undefined {
  const normalized = normalizeAddress(address)
  if (!normalized) {
    return undefined
  }
  const tokenize = (value: string): string[] =>
    value
      .toUpperCase()
      .replace(/[.,]/g, ' ')
      .replace(/#/g, ' # ')
      .split(/\s+/)
      .filter(Boolean)
      .map(standardizeAddressToken)
  const streetTokens = tokenize(normalized.line?.[0] ?? '')
  const unitIndex = streetTokens.findIndex(
    (token, i) => i >= 2 && (token === '#' || UNIT_DESIGNATORS.has(token))
  )
  const street =
    unitIndex === -1 ? streetTokens : streetTokens.slice(0, unitIndex)
  return [
    street.join(' '),
    tokenize(normalized.city ?? '').join(' '),
    (normalized.postalCode ?? '').slice(0, 5),
  ].join('|')
}

function postalCodesEqual(
  a: string | undefined,
  b: string | undefined
): boolean {
  if (!a && !b) {
    return true
  }
  if (!a || !b) {
    return false
  }
  const aHasPlus4 = /-\d{4}$/.test(a)
  const bHasPlus4 = /-\d{4}$/.test(b)
  if (aHasPlus4 && bHasPlus4) {
    return a === b
  }
  return a.slice(0, 5) === b.slice(0, 5)
}

function linesEqual(a: string[] | undefined, b: string[] | undefined): boolean {
  const aLines = a ?? []
  const bLines = b ?? []
  if (aLines.length !== bLines.length) {
    return false
  }
  for (let i = 0; i < aLines.length; i++) {
    if (aLines[i] !== bLines[i]) {
      return false
    }
  }
  return true
}

export function addressesEqual(
  a: Address | undefined,
  b: Address | undefined
): boolean {
  const na = normalizeAddress(a)
  const nb = normalizeAddress(b)
  if (!na && !nb) {
    return true
  }
  if (!na || !nb) {
    return false
  }
  return (
    linesEqual(na.line, nb.line) &&
    (na.city ?? '') === (nb.city ?? '') &&
    (na.state ?? '') === (nb.state ?? '') &&
    postalCodesEqual(na.postalCode, nb.postalCode)
  )
}
